import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { BrandProvider } from "./contexts/BrandContext";
import { NotificationsProvider } from "./contexts/NotificationsContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { ModulesProvider } from "./hooks/useModules";
import { Toaster } from "./components/ui/sonner";

// Layout
import AppLayout from "./components/layout/AppLayout";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import OnboardingPage from "./pages/OnboardingPage";
import RoleAwareOnboardingEntry from "./pages/RoleAwareOnboardingEntry";
import OnboardingIntroPage from "./pages/OnboardingIntroPage";
import DashboardPage from "./pages/DashboardPage";
import RoleAwareDashboard from "./pages/RoleAwareDashboard";
import SocialMediaPage from "./pages/SocialMediaPage";
import ContentStudioPage from "./pages/ContentStudioPage";
import KoolKonnectPage from "./pages/KoolKonnectPage";
import KoolKonnectProjectPage from "./pages/KoolKonnectProjectPage";
import KoolKonnectMagicPage from "./pages/KoolKonnectMagicPage";
import ReputationPage from "./pages/ReputationPage";
import SEOPage from "./pages/SEOPage";
import EmailMarketingPage from "./pages/EmailMarketingPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import PaidAdsPage from "./pages/PaidAdsPage";
import WebsiteManagerPage from "./pages/WebsiteManagerPage";
import ADACompliancePage from "./pages/ADACompliancePage";
import SettingsPage from "./pages/SettingsPage";
import UsageAnalyticsPage from "./pages/UsageAnalyticsPage";
import PerformanceLoopPage from "./pages/PerformanceLoopPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import AdminPortalPage from "./pages/AdminPortalPage";
import VoiceReceptionistPage from "./pages/VoiceReceptionistPage";
import PublicVoicePage from "./pages/PublicVoicePage";
import ModulePage from "./pages/ModulePage";
import LeadPipelinePage from "./pages/LeadPipelinePage";
import LeadCaptureFormsPage from "./pages/LeadCaptureFormsPage";
import PublicFormPage from "./pages/PublicFormPage";
import PublicLandingPage from "./pages/PublicLandingPage";
import ReviewSmartLinkPage from "./pages/ReviewSmartLinkPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import SetPasswordPage from "./pages/SetPasswordPage";
import WelcomeOrientationPage from "./pages/WelcomeOrientationPage";
import LeadAutomationPage from "./pages/LeadAutomationPage";
import QRGeneratorPage from "./pages/QRGeneratorPage";
import CardScannerPage from "./pages/CardScannerPage";
import LeadInsightsPage from "./pages/LeadInsightsPage";
import ProductCatalogPage from "./pages/ProductCatalogPage";
import ProposalsPage from "./pages/ProposalsPage";
import ProposalCreatorPage from "./pages/ProposalCreatorPage";
import ProposalAnalyticsPage from "./pages/ProposalAnalyticsPage";
import MyContactsPage from "./pages/MyContactsPage";
import PromotionsPage from "./pages/PromotionsPage";
import EmployeeDatabasePage from "./pages/EmployeeDatabasePage";
// v3.13 — Employee self-service portal (team_member role)
import MyDashboardPage from "./pages/employee/MyDashboardPage";
import MyProfilePage from "./pages/employee/MyProfilePage";
import MyAttendancePage from "./pages/employee/MyAttendancePage";
import MyLeavePage from "./pages/employee/MyLeavePage";
import MyPayrollPage from "./pages/employee/MyPayrollPage";
import MyBenefitsPage from "./pages/employee/MyBenefitsPage";
import MyTrainingPage from "./pages/employee/MyTrainingPage";
import MyGoalsPage from "./pages/employee/MyGoalsPage";
import MyExpensesPage from "./pages/employee/MyExpensesPage";
import KPIManagerPage from "./pages/KPIManagerPage";
import PerformanceEvaluationPage from "./pages/PerformanceEvaluationPage";
import TaskManagerPage from "./pages/TaskManagerPage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import AlertManagementPage from "./pages/AlertManagementPage";
import HelpDeskPage from "./pages/HelpDeskPage";
import KnowledgeBasePage from "./pages/KnowledgeBasePage";
import LiveChatPage from "./pages/LiveChatPage";
import SurveysPage from "./pages/SurveysPage";
import LoyaltyPage from "./pages/LoyaltyPage";
import CelebrationsPage from "./pages/CelebrationsPage";
import ReferralsPage from "./pages/ReferralsPage";
import WinbackPage from "./pages/WinbackPage";
import SalesPipelinePage from "./pages/SalesPipelinePage";
import CommissionTrackerPage from "./pages/CommissionTrackerPage";
import CalendarPage from "./pages/CalendarPage";
import DocumentVaultPage from "./pages/DocumentVaultPage";
// WorkflowsPage merged into AutomationCenterPage (v2.5.897). Route below redirects.
import AutomationDashboardPage from "./pages/AutomationDashboardPage";
import EscalationDashboardPage from "./pages/EscalationDashboardPage";
import TemplateLibraryPage from "./pages/TemplateLibraryPage";
import KPIDashboardPage from "./pages/KPIDashboardPage";
import LetterheadPage from "./pages/LetterheadPage";
import SmartSetupPage from "./pages/SmartSetupPage";
import MediaManagementPage from "./pages/MediaManagementPage";
import MessagingChannelsPage from "./pages/MessagingChannelsPage";
import AffiliatesPage from "./pages/AffiliatesPage";
import EventsPage from "./pages/EventsPage";
import LandingPagesPage from "./pages/LandingPagesPage";
import BusinessIntelligencePage from "./pages/BusinessIntelligencePage";
import CustomReportsPage from "./pages/CustomReportsPage";
import UniversalReportDashboardPage from "./pages/UniversalReportDashboardPage";
import SecuritySettingsPage from "./pages/SecuritySettingsPage";
import PublicProposalPage from "./pages/PublicProposalPage";
import StaffManagementPage from "./pages/StaffManagementPage";
import AttendancePayrollPage from "./pages/AttendancePayrollPage";
import QRManagementPage from "./pages/QRManagementPage";
import ManagementHubPage from "./pages/ManagementHubPage";
import PublicMeetingPage from "./pages/PublicMeetingPage";
import PublicShowcasePage from "./pages/PublicShowcasePage";
import ReleaseNotesPage from "./pages/ReleaseNotesPage";
import DeployHealthPage from "./pages/settings/DeployHealthPage";
import InquiryInboxPage from "./pages/InquiryInboxPage";

// Batch 1 Pages
import OnboardingChecklistPage from "./pages/OnboardingChecklistPage";
import InvoiceGeneratorPage from "./pages/InvoiceGeneratorPage";
import ESignaturePage from "./pages/ESignaturePage";
import FollowUpRemindersPage from "./pages/FollowUpRemindersPage";
import CampaignCalendarPage from "./pages/CampaignCalendarPage";
import OmnichannelInboxPage from "./pages/OmnichannelInboxPage";
import DispatchSchedulingPage from "./pages/DispatchSchedulingPage";
import FieldJobsPage from "./pages/FieldJobsPage";
import FieldGPSPage from "./pages/FieldGPSPage";
import FieldKPIsPage from "./pages/FieldKPIsPage";
import FieldSchedulePage from "./pages/FieldSchedulePage";
import ClientsPage from "./pages/ClientsPage";
import HRPage from "./pages/HRPage";
import BookingPage from "./pages/BookingPage";
import PublicBookingPage from "./pages/PublicBookingPage";
import ManageBookingPage from "./pages/ManageBookingPage";
import AuditLogPage from "./pages/AuditLogPage";
import GoalTrackingPage from "./pages/GoalTrackingPage";
import LeadSourceTrackerPage from "./pages/LeadSourceTrackerPage";

// Batch 2 Pages
import ChurnPredictionPage from "./pages/ChurnPredictionPage";
import VIPTiersPage from "./pages/VIPTiersPage";
import NPSSurveyPage from "./pages/NPSSurveyPage";
import AIChatbotPage from "./pages/AIChatbotPage";
import CallRecordingPage from "./pages/CallRecordingPage";
import SLAManagementPage from "./pages/SLAManagementPage";

// Batch 3 Pages
// SEO Audit merged into SEO Center (tab in SEOPage)
import HeatmapsPage from "./pages/HeatmapsPage";
import CROPage from "./pages/CROPage";
import DataExportPage from "./pages/DataExportPage";
import MigrationWizardPage from "./pages/MigrationWizardPage";
import IntegrationsHubPage from "./pages/IntegrationsHubPage";
import VideoGenPage from "./pages/VideoGenPage";
import UTMBuilderPage from "./pages/UTMBuilderPage";

// Phase 4 Pages
import CommandCenterPage from "./pages/CommandCenterPage";
import GlobalSettingsPage from "./pages/GlobalSettingsPage";
import SettingsEnginePage from "./pages/SettingsEnginePage";
import FormBuilderPage from "./pages/FormBuilderPage";
import OAuthProvidersAdminPage from "./pages/OAuthProvidersAdminPage";
import FinancePage from "./pages/FinancePage";
import FinanceExpensesPage from "./pages/FinanceExpensesPage";
import FinanceBillsPage from "./pages/FinanceBillsPage";
import FinanceReportsPage from "./pages/FinanceReportsPage";
import FinanceTaxPage from "./pages/FinanceTaxPage";
import AdAccountsPage from "./pages/AdAccountsPage";
import AICreditsPage from "./pages/AICreditsPage";
import CreditManagementPage from "./pages/CreditManagementPage";
import MeetingPage from "./pages/MeetingPage";
import MeetingRoomPage from "./pages/MeetingRoomPage";
import MeetingMinutesPage from "./pages/MeetingMinutesPage";
import PromptPowerUpPage from "./pages/PromptPowerUpPage";
import ScheduledReportsPage from "./pages/ScheduledReportsPage";
import AIInsightsPage from "./pages/AIInsightsPage";
import EfficiencyROIPage from "./pages/EfficiencyROIPage";
import WhitelabelPage from "./pages/WhitelabelPage";
import MyWhitelabelPage from "./pages/MyWhitelabelPage";
import LandingContentEditorPage from "./pages/LandingContentEditorPage";
import AutomationCenterPage from "./pages/AutomationCenterPage";
import OnboardingWizardPage from "./pages/OnboardingWizardPage";
import ContentQueuePage from "./pages/ContentQueuePage";
import BrandVoicePage from "./pages/BrandVoicePage";
import VendingFleetPage from "./pages/VendingFleetPage";
import VendingTelemetryPage from "./pages/VendingTelemetryPage";
import VendingPlanogramsPage from "./pages/VendingPlanogramsPage";
import VendingDexCompanionPage from "./pages/VendingDexCompanionPage";
import VendingPlanogramEditorPage from "./pages/VendingPlanogramEditorPage";
import VendingRoutingPage from "./pages/VendingRoutingPage";
import VendingReconciliationPage from "./pages/VendingReconciliationPage";
import VendingDashboardTvPage from "./pages/VendingDashboardTvPage";
import VendingCommissionsPage from "./pages/VendingCommissionsPage";
import VendingContractsPage from "./pages/VendingContractsPage";
import VendingContractSignPage from "./pages/VendingContractSignPage";
import AdminAddonGrantPage from "./pages/AdminAddonGrantPage";
import ReferenceDataPage from "./pages/ReferenceDataPage";
import WhatsAppPage from "./pages/WhatsAppPage";
import InfluencerPage from "./pages/InfluencerPage";
import SalesDeckPage from "./pages/SalesDeckPage";
import PublicSalesDeckPage from "./pages/PublicSalesDeckPage";
import KaharianHealthPage from "./pages/KaharianHealthPage";
import DevConsolePage from "./pages/DevConsolePage";
import UIBugQueuePage from "./pages/UIBugQueuePage";
import SystemHealthPage from "./pages/SystemHealthPage";
import ModuleManagerPage from "./pages/ModuleManagerPage";
import ComingSoonPage from "./pages/ComingSoonPage";
import MarketplacePage from "./pages/MarketplacePage";

import "./App.css";

// Error Boundary to prevent blank pages from JS crashes
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('ErrorBoundary caught:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      const errMsg = this.state.error?.message || 'Unknown error';
      const errStack = this.state.error?.stack || '';
      const componentStack = this.state.errorInfo?.componentStack || '';
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', fontFamily: 'system-ui, sans-serif', padding: '2rem' }}>
          <div style={{ textAlign: 'center', maxWidth: 600 }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>!</div>
            <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.5rem' }}>Something went wrong</h1>
            <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1rem' }}>Please try refreshing the page.</p>
            <button onClick={() => { this.setState({ hasError: false, error: null, errorInfo: null }); window.location.reload(); }} style={{ padding: '0.625rem 1.5rem', background: '#10b981', color: '#fff', border: 'none', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem', marginBottom: '1rem' }}>
              Refresh Page
            </button>
            <details style={{ textAlign: 'left', background: '#f1f5f9', borderRadius: '0.5rem', padding: '1rem', marginTop: '1rem', fontSize: '0.75rem', color: '#475569', maxHeight: '200px', overflow: 'auto' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Error Details (for support)</summary>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginTop: '0.5rem' }}>{errMsg}{'\n'}{errStack}{componentStack && '\n\nComponent:\n' + componentStack}</pre>
            </details>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Protected Route Component
const ProtectedRoute = ({ children, skipSpqCheck = false, skipPwCheck = false, skipOrientationCheck = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="text-center">
          <img src="/konnect-logo.png" alt="Konnect" className="h-10 w-auto object-contain mx-auto mb-4 animate-pulse" />
          <p className="text-gray-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // First-priority gate: force password change for invited employees
  // (and anyone an admin has flagged with must_change_password=true).
  if (!skipPwCheck && user.must_change_password) {
    return <Navigate to="/set-password?welcome=1" replace />;
  }

  // Second-priority gate: orientation walkthrough for newly-invited employees.
  if (!skipOrientationCheck && user.needs_orientation && !user.must_change_password) {
    return <Navigate to="/welcome" replace />;
  }

  // Redirect to Smart Setup if SPQ not completed (except for the SPQ page itself).
  // team_member roles never need SPQ — that's a subscriber-admin step.
  if (!skipSpqCheck && !user.spq_completed && user.role !== 'super_admin' && user.role !== 'team_member') {
    return <Navigate to="/smart-setup" replace />;
  }

  return children;
};

// Public Route (redirect if authenticated)
// EXCEPTION: when the URL carries a magic-link / legacy token param, we must
// NOT auto-redirect — otherwise an already-logged-in user clicking a magic
// link in their browser gets bounced to /dashboard under the OLD identity
// before LoginPage can mount and consume the new token. This caused a serious
// "logged in as the wrong user" incident (2026-06-23). Letting LoginPage
// render in this case ensures its consume-magic-link handler runs and
// forcibly overrides the existing session.
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const hasMagicLink = /[?&](mlt|token)=/.test(search);

  if (loading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="text-center">
          <img src="/konnect-logo.png" alt="Konnect" className="h-10 w-auto object-contain mx-auto mb-4 animate-pulse" />
          <p className="text-gray-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (user && !hasMagicLink) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Placeholder pages for routes not yet fully implemented
const PlaceholderPage = ({ title }) => (
  <div className="space-y-6 animate-fadeIn">
    <div>
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">{title}</h1>
      <p className="text-gray-medium">This feature is coming soon</p>
    </div>
    <div className="bg-white border border-gray-border rounded-xl p-8 text-center">
      <p className="text-gray-medium">We're working on this feature. Check back soon!</p>
    </div>
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/voice/:tenantSlug" element={<PublicVoicePage />} />
      <Route path="/p/:tenantSlug/:pageSlug" element={<PublicLandingPage />} />
      <Route path="/r/:token" element={<ReviewSmartLinkPage />} />
      <Route path="/form/:slug" element={<PublicFormPage />} />
      <Route path="/proposal/:token" element={<PublicProposalPage />} />
      <Route path="/join/:token" element={<PublicMeetingPage />} />
      <Route path="/showcase/:token" element={<PublicShowcasePage />} />
      <Route path="/p/deck/:typeId" element={<PublicSalesDeckPage />} />
      <Route path="/p/deck" element={<PublicSalesDeckPage />} />
      <Route path="/public/marketplace" element={<MarketplacePage />} />
      {/* v2.5.912 — Public contract signing (Konnect Sign) */}
      <Route path="/vending/contracts/sign/:token" element={<VendingContractSignPage />} />
      {/* v2.6.36 — Shareable public cinematic tour (no login) */}
      <Route path="/tour" element={<OnboardingIntroPage mode="public" />} />
      <Route path="/tour/konnect" element={<OnboardingIntroPage mode="public" />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
      <Route path="/book/:tenantId" element={<PublicBookingPage />} />
      <Route path="/booking/manage/:tenantId/:bookingId" element={<ManageBookingPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Invite magic-link landing — public; auto-logs in via invite_code */}
      <Route path="/accept-invite/:inviteCode" element={<AcceptInvitePage />} />

      {/* First-time set-password (after invite, or whenever must_change_password=true) */}
      <Route path="/set-password" element={
        <ProtectedRoute skipSpqCheck={true} skipPwCheck={true} skipOrientationCheck={true}>
          <SetPasswordPage />
        </ProtectedRoute>
      } />

      {/* Welcome / orientation walkthrough for invited employees */}
      <Route path="/welcome" element={
        <ProtectedRoute skipSpqCheck={true} skipOrientationCheck={true}>
          <WelcomeOrientationPage />
        </ProtectedRoute>
      } />

      {/* Smart Pre-Onboarding Questionnaire (outside main layout) */}
      <Route path="/smart-setup" element={<ProtectedRoute skipSpqCheck={true}><SmartSetupPage /></ProtectedRoute>} />

      {/* Role-aware onboarding entry — dispatches to the right wizard by role.
          team_member → /welcome, country_operator → Country wizard,
          client_admin → Subscriber wizard (Sprint 2), super_admin → /command-center. */}
      <Route path="/onboarding" element={<ProtectedRoute><RoleAwareOnboardingEntry /></ProtectedRoute>} />
      <Route path="/welcome-intro" element={<ProtectedRoute><OnboardingIntroPage /></ProtectedRoute>} />
      {/* Legacy marketing-onboarding kept accessible for CMS-authored campaigns only. */}
      <Route path="/onboarding-legacy" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />

      {/* Protected routes with AppLayout */}
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        {/* Overview */}
        <Route path="/dashboard" element={<RoleAwareDashboard />} />
        <Route path="/command-center" element={<CommandCenterPage />} />
        <Route path="/admin" element={<AdminPortalPage />} />
        <Route path="/overview/onboarding" element={<OnboardingChecklistPage />} />
        <Route path="/marketplace" element={<MarketplacePage />} />

        {/* Lead Generation */}
        <Route path="/leads/pipeline" element={<LeadPipelinePage />} />
        <Route path="/leads/automation" element={<LeadAutomationPage />} />
        <Route path="/leads/source-tracker" element={<LeadSourceTrackerPage />} />
        <Route path="/leads/capture-forms" element={<LeadCaptureFormsPage />} />
        <Route path="/leads/card-scanner" element={<CardScannerPage />} />
        <Route path="/leads/qr-generator" element={<Navigate to="/leads/qr-management" replace />} />
        <Route path="/leads/qr-management" element={<QRManagementPage />} />
        <Route path="/management-hub" element={<ManagementHubPage />} />
        <Route path="/leads/ai-insights" element={<LeadInsightsPage />} />

        {/* Sales */}
        <Route path="/sales/catalog" element={<ProductCatalogPage />} />
        <Route path="/sales/inquiries" element={<InquiryInboxPage />} />
        <Route path="/sales/proposals" element={<ProposalsPage />} />
        <Route path="/sales/proposal-creator" element={<Navigate to="/sales/proposals" replace />} />
        <Route path="/sales/proposal-analytics" element={<Navigate to="/sales/proposals" replace />} />
        <Route path="/sales/my-contacts" element={<Navigate to="/leads/pipeline" replace />} />
        <Route path="/sales/promotions" element={<Navigate to="/sales/catalog" replace />} />
        <Route path="/sales/pipeline" element={<SalesPipelinePage />} />
        <Route path="/sales/commissions" element={<CommissionTrackerPage />} />
        <Route path="/sales/invoices" element={<InvoiceGeneratorPage />} />
        <Route path="/sales/e-signature" element={<ESignaturePage />} />
        <Route path="/sales/follow-ups" element={<FollowUpRemindersPage />} />

        {/* Marketing (existing + new) */}
        <Route path="/social" element={<SocialMediaPage />} />
        <Route path="/content" element={<ContentStudioPage />} />
        {/* v2.6.39 — new default = KoolKonnect Magic (one-click multi-output studio) */}
        <Route path="/koolkonnect" element={<KoolKonnectMagicPage />} />
        <Route path="/koolkonnect/magic" element={<KoolKonnectMagicPage />} />
        <Route path="/koolkonnect/projects" element={<KoolKonnectPage />} />
        <Route path="/koolkonnect/project/:pid" element={<KoolKonnectProjectPage />} />
        <Route path="/email" element={<EmailMarketingPage />} />
        <Route path="/ads" element={<PaidAdsPage />} />
        <Route path="/seo" element={<SEOPage />} />
        <Route path="/marketing/messaging" element={<MessagingChannelsPage />} />
        <Route path="/marketing/sms-whatsapp" element={<Navigate to="/marketing/messaging" replace />} />
        <Route path="/marketing/push-notifications" element={<Navigate to="/marketing/messaging" replace />} />
        <Route path="/marketing/direct-mail" element={<Navigate to="/marketing/messaging" replace />} />
        <Route path="/marketing/affiliates" element={<AffiliatesPage />} />
        <Route path="/marketing/events" element={<EventsPage />} />
        <Route path="/marketing/campaign-calendar" element={<CampaignCalendarPage />} />
        <Route path="/marketing/video-gen" element={<Navigate to="/content" replace />} />
        <Route path="/marketing/utm-builder" element={<Navigate to="/marketing/campaign-calendar" replace />} />

        {/* Customer Retention */}
        <Route path="/retention/loyalty" element={<LoyaltyPage />} />
        <Route path="/retention/celebrations" element={<CelebrationsPage />} />
        <Route path="/retention/referrals" element={<ReferralsPage />} />
        <Route path="/retention/winback" element={<WinbackPage />} />
        <Route path="/retention/churn-prediction" element={<ChurnPredictionPage />} />
        <Route path="/retention/vip-tiers" element={<VIPTiersPage />} />
        <Route path="/retention/nps" element={<Navigate to="/service/surveys" replace />} />

        {/* Customer Service */}
        <Route path="/voice-receptionist" element={<VoiceReceptionistPage />} />
        <Route path="/service/helpdesk" element={<HelpDeskPage />} />
        <Route path="/service/live-chat" element={<LiveChatPage />} />
        <Route path="/service/knowledge-base" element={<KnowledgeBasePage />} />
        <Route path="/service/surveys" element={<SurveysPage />} />
        <Route path="/service/inbox" element={<OmnichannelInboxPage />} />
        <Route path="/service/ai-chatbot" element={<AIChatbotPage />} />
        <Route path="/service/call-recordings" element={<CallRecordingPage />} />
        <Route path="/service/sla" element={<SLAManagementPage />} />

        {/* Operations */}
        <Route path="/operations/staff" element={<StaffManagementPage />} />
        <Route path="/operations/attendance" element={<AttendancePayrollPage />} />
        {/* v3.13 — Employee self-service portal (team_member role) */}
        <Route path="/employee/dashboard" element={<MyDashboardPage />} />
        <Route path="/employee/profile" element={<MyProfilePage />} />
        {/* v3.15 — Employee sub-pages linked from MyDashboardPage quick actions */}
        <Route path="/employee/attendance" element={<MyAttendancePage />} />
        <Route path="/employee/leave" element={<MyLeavePage />} />
        <Route path="/employee/payroll" element={<MyPayrollPage />} />
        <Route path="/employee/benefits" element={<MyBenefitsPage />} />
        <Route path="/employee/training" element={<MyTrainingPage />} />
        <Route path="/employee/goals" element={<MyGoalsPage />} />
        <Route path="/employee/expenses" element={<MyExpensesPage />} />
        <Route path="/operations/employees" element={<Navigate to="/operations/staff" replace />} />
        <Route path="/operations/performance" element={<Navigate to="/operations/evaluations" replace />} />
        <Route path="/operations/evaluations" element={<PerformanceEvaluationPage />} />
        <Route path="/operations/tasks" element={<TaskManagerPage />} />
        <Route path="/operations/calendar" element={<CalendarPage />} />
        <Route path="/operations/documents" element={<DocumentVaultPage />} />
        <Route path="/operations/workflows" element={<Navigate to="/automation-center" replace />} />
        <Route path="/operations/automation" element={<AutomationDashboardPage />} />
        <Route path="/hr" element={<HRPage />} />
        <Route path="/booking" element={<BookingPage />} />
        <Route path="/hr/escalation" element={<EscalationDashboardPage />} />
        <Route path="/tools/template-library" element={<TemplateLibraryPage />} />
        <Route path="/hr/kpi-dashboard" element={<KPIDashboardPage />} />
        <Route path="/settings/letterheads" element={<LetterheadPage />} />
        <Route path="/content/media" element={<MediaManagementPage />} />
        <Route path="/operations/announcements" element={<AnnouncementsPage />} />
        <Route path="/operations/dispatch" element={<Navigate to="/operations/field-jobs" replace />} />
        <Route path="/operations/field-jobs" element={<FieldJobsPage />} />
        <Route path="/operations/field-gps" element={<FieldGPSPage />} />
        <Route path="/operations/field-kpis" element={<Navigate to="/operations/evaluations" replace />} />
        <Route path="/operations/field-schedule" element={<FieldSchedulePage />} />
        <Route path="/sales/clients" element={<ClientsPage />} />
        <Route path="/operations/hr" element={<Navigate to="/hr" replace />} />
        <Route path="/operations/alert-management" element={<AlertManagementPage />} />

        {/* Website & Compliance */}
        <Route path="/website" element={<WebsiteManagerPage />} />
        <Route path="/ada" element={<ADACompliancePage />} />
        <Route path="/website/landing-pages" element={<LandingPagesPage />} />
        <Route path="/website/seo-audit" element={<Navigate to="/seo" replace />} />
        <Route path="/website/heatmaps" element={<HeatmapsPage />} />
        <Route path="/website/cro" element={<CROPage />} />

        {/* Analytics & Reports */}
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/analytics/intelligence" element={<BusinessIntelligencePage />} />
        <Route path="/analytics/reports" element={<CustomReportsPage />} />
        <Route path="/analytics/report-dashboard" element={<UniversalReportDashboardPage />} />
        <Route path="/usage" element={<UsageAnalyticsPage />} />
        <Route path="/analytics/performance-loop" element={<PerformanceLoopPage />} />
        <Route path="/analytics/goals" element={<GoalTrackingPage />} />

        {/* Platform Settings */}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/global" element={<GlobalSettingsPage />} />
        <Route path="/settings/config" element={<SettingsEnginePage />} />
        <Route path="/settings/form-builder" element={<FormBuilderPage />} />
        {/* Legacy /settings/integrations deprecated — single source of truth is /settings/oauth-apps */}
        <Route path="/settings/integrations" element={<Navigate to="/settings/oauth-apps" replace />} />
        <Route path="/settings/integrations-hub" element={<Navigate to="/settings/oauth-apps" replace />} />
        <Route path="/settings/oauth-apps" element={<OAuthProvidersAdminPage />} />
        <Route path="/finance" element={<FinancePage />} />
        <Route path="/finance/expenses" element={<FinanceExpensesPage />} />
        <Route path="/finance/bills" element={<FinanceBillsPage />} />
        <Route path="/finance/reports" element={<FinanceReportsPage />} />
        <Route path="/finance/tax" element={<FinanceTaxPage />} />
        <Route path="/settings/ad-accounts" element={<AdAccountsPage />} />
        <Route path="/settings/credits" element={<AICreditsPage />} />
        <Route path="/settings/credit-management" element={<Navigate to="/settings/credits" replace />} />
        <Route path="/meeting" element={<MeetingPage />} />
        <Route path="/meeting/:meetingId" element={<MeetingRoomPage />} />
        <Route path="/meeting/:meetingId/minutes" element={<MeetingMinutesPage />} />
        <Route path="/prompt-powerup" element={<PromptPowerUpPage />} />
        <Route path="/settings/whitelabel" element={<WhitelabelPage />} />
        <Route path="/settings/my-whitelabel" element={<MyWhitelabelPage />} />
        <Route path="/settings/landing-editor" element={<LandingContentEditorPage />} />
        <Route path="/automation-center" element={<AutomationCenterPage />} />
        <Route path="/content-queue" element={<ContentQueuePage />} />
        <Route path="/marketing/brand-voice" element={<BrandVoicePage />} />
        <Route path="/vending" element={<VendingFleetPage />} />
        <Route path="/vending/machines" element={<VendingFleetPage />} />
        <Route path="/vending/locations" element={<VendingFleetPage />} />
        <Route path="/vending/clients" element={<VendingFleetPage />} />
        <Route path="/vending/products" element={<VendingFleetPage />} />
        <Route path="/vending/telemetry" element={<VendingTelemetryPage />} />
        <Route path="/vending/planograms" element={<VendingPlanogramsPage />} />
        <Route path="/vending/planogram-editor" element={<VendingPlanogramEditorPage />} />
        <Route path="/vending/routing" element={<VendingRoutingPage />} />
        <Route path="/vending/reconciliation" element={<VendingReconciliationPage />} />
        <Route path="/vending/dashboard-tv" element={<VendingDashboardTvPage />} />
        <Route path="/vending/companion" element={<VendingDexCompanionPage />} />
        <Route path="/vending/contracts" element={<VendingContractsPage />} />
        <Route path="/vending/commissions" element={<VendingCommissionsPage />} />
        <Route path="/settings/addon-grants" element={<AdminAddonGrantPage />} />
        <Route path="/settings/reference-data" element={<ReferenceDataPage />} />
        <Route path="/whatsapp" element={<WhatsAppPage />} />
        <Route path="/influencer" element={<InfluencerPage />} />
        <Route path="/sales-deck" element={<SalesDeckPage />} />
        <Route path="/analytics/scheduled-reports" element={<ScheduledReportsPage />} />
        <Route path="/analytics/ai-insights" element={<AIInsightsPage />} />
        <Route path="/analytics/efficiency-roi" element={<EfficiencyROIPage />} />
        <Route path="/settings/billing" element={<Navigate to="/settings/credits" replace />} />
        <Route path="/settings/security" element={<SecuritySettingsPage />} />
        <Route path="/settings/audit-log" element={<AuditLogPage />} />
        <Route path="/settings/data-export" element={<DataExportPage />} />
        <Route path="/settings/migration" element={<MigrationWizardPage />} />
        <Route path="/settings/api-health" element={<KaharianHealthPage />} />
        <Route path="/dev-console" element={<DevConsolePage />} />
        <Route path="/admin/ui-bug-queue" element={<UIBugQueuePage />} />
        <Route path="/settings/system-health" element={<SystemHealthPage />} />
        <Route path="/settings/module-manager" element={<ModuleManagerPage />} />
        <Route path="/coming-soon/*" element={<ComingSoonPage />} />
        <Route path="/settings/release-notes" element={<ReleaseNotesPage />} />
        <Route path="/settings/deploy-health" element={<DeployHealthPage />} />
        <Route path="/reputation" element={<ReputationPage />} />

        {/* Legacy routes */}
        <Route path="/integrations" element={<Navigate to="/settings/integrations" replace />} />
        <Route path="/team" element={<Navigate to="/operations/staff" replace />} />
        <Route path="/help" element={<PlaceholderPage title="Help & Support" />} />
      </Route>

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

import { ActivityMonitor } from "./components/ActivityMonitor";
import { TourProvider } from "./contexts/TourContext";
import { SampleDataTour } from "./components/SampleDataTour";
import { WelcomeOnboarding } from "./components/WelcomeOnboarding";
import { HubPricingProvider } from "./lib/useHubPricing";
import GoogleAnalyticsLoader from "./components/GoogleAnalyticsLoader";

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <BrandProvider>
          <ModulesProvider>
          <NotificationsProvider>
            <CurrencyProvider>
              <HubPricingProvider>
                <TourProvider>
                  <AppRoutes />
                  <ActivityMonitor />
                  <SampleDataTour />
                  <WelcomeOnboarding />
                  <GoogleAnalyticsLoader />
                  <Toaster position="top-right" />
                </TourProvider>
              </HubPricingProvider>
            </CurrencyProvider>
          </NotificationsProvider>
          </ModulesProvider>
          </BrandProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;

